import { params } from "@/params";
import { useWorkspace } from "@/routes/simulator/context/wrokspace";
import {
  CheckIcon,
  DotsHorizontalIcon,
  GlobeIcon,
} from "@radix-ui/react-icons";
import {
  Badge,
  Button,
  Card,
  Dialog,
  Flex,
  Grid,
  Spinner,
  Text,
} from "@radix-ui/themes";
import cx from "classnames";
import { FileIcon, Plus, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Integration,
  IntegrationName,
  IntegrationType,
  isIntegrationCompleted,
  isIntegrationNotAvailable,
  supportedTicketSystems,
  supportedWiki,
  TicketSystemIntegrationName,
  WikiIntegrationName,
} from "../../../../api/integration/Integration.model";
import { integrationNameToGalleryItem } from "../../../../api/integration/integrationNameToGalleryItem";
import styles from "../AddIntegration.module.scss";
import { AddIntegrationConfig } from "../addIntegrationConfigs";
import { StepContent, StepFooter, StepHeader, StepMain } from "../parts";
import Switch from "@/components/shared/inputs/switch/Switch";
import {
  useGetJobs,
  useDeleteIntegration,
} from "@/api/integration/Integration.api";
import { useBrands } from "@/api/useBrands";
import { useKnowledgeFileUpload } from "@/api/useKnowledgeFileUpload";
import { Collapsable } from "@/components/collapsable";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import Checkbox from "@/components/shared/inputs/checkbox";
import Select from "@/components/shared/select";
import { KnowledgeFileUploadComponent } from "@/components/shared/upload-file";
import { FieldGroup } from "@/features/automation-configuration/parts/FieldGroup";
import { Tabs, TabsList, TabsTrigger } from "@/v2/components/dataTabs/tabs";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { ZendeskKnowledgeIntegration } from "./ZendeskKnowledgeIntegration";
import { ZendeskTicketIntegration } from "./ZendeskTicketIntegration";
import { ConfigureAccessDialog } from "./ConfigureAccessDialog";
import { validateFile } from "@/utils/fileValidation";

export interface SelectIntegrationProps {
  type: IntegrationType;
  existingIntegrations: Integration[];
  isMultipleAllowed?: boolean;
  onClick: (name: IntegrationName, type: IntegrationType) => void;
  onClose?: () => void;
}

const integrationTypeToContent: Record<
  IntegrationType,
  { title: string; description: string }
> = {
  CHAT: {
    title: "Live Support",
    description:
      "Integrate with your live support software to enable escalation to live agents.",
  },
  KNOWLEDGE_BASE: {
    title: "Knowledge & Docs (External)",
    description:
      "Connect to give Quack access to information for analysis and smarter responses.",
  },
  TICKET_SYSTEM: {
    title: "Ticketing system",
    description:
      "Connect to manage, track, and escalate tickets through Quack.",
  },
  TASK_MANAGEMENT: {
    title: "Task Management system",
    description: "Connect to help Quack track issues and understand workflows.",
  },
  WIKI: {
    title: "Wiki (Internal)",
    description:
      "Connect to give Quack Copilot access to internal documentations",
  },
};

function SelectIntegration({
  type,
  existingIntegrations,
  title,
  description,
  options,
  isMultipleAllowed = false,
  onClick,
  onClose,
}: SelectIntegrationProps & AddIntegrationConfig["select"]) {
  const { workspace } = useWorkspace();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [isSyncOnlyOnePage, setIsSyncOnlyOnePage] = useState(false);
  const [uploadMode, setUploadMode] = useState<"url" | "file">("url");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileTitle, setFileTitle] = useState("");
  const [knowledgeType, setKnowledgeType] = useState<"kb" | "wiki">("kb");
  const [allowFullAccess, setAllowFullAccess] = useState<boolean>(true);
  const [isConfigureDialogOpen, setIsConfigureDialogOpen] = useState(false);
  const [isConfigureTicketDialogOpen, setIsConfigureTicketDialogOpen] =
    useState(false);
  const [isConfigureAccessDialogOpen, setIsConfigureAccessDialogOpen] =
    useState(false);
  const [selectedIntegrationForAccess, setSelectedIntegrationForAccess] =
    useState<Integration | null>(null);
  const { mutateAsync: deleteIntegration } = useDeleteIntegration();

  const { data: brands } = useBrands();

  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);

  const { data: jobs, refetch } = useGetJobs();
  const { mutateAsync: uploadFile, isLoading: isUploading } =
    useKnowledgeFileUpload();

  async function handleSubmit(urlToUse?: string) {
    const finalUrl = urlToUse || url;
    try {
      const response = await fetch("https://api.firecrawl.dev/v2/crawl", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer fc-4d72d767feac4c48a3db39bb6abb6a60",
        },
        body: JSON.stringify({
          url: finalUrl,
          limit: isSyncOnlyOnePage ? 1 : 1500,
          webhook: {
            url: `${params.API_URL}/knowledge-base-handler/webhook/articles`,
            metadata: {
              tenant: workspace,
              url: finalUrl,
              brand: String(selectedBrand),
              noAccessToRelease: String(!allowFullAccess),
            },
            headers: {
              "x-api-key": params.KB_WEBHOOK_KEY,
            },
            events: ["started", "page", "completed"],
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to start crawling");
      }

      const data = await response.json();

      setIsDialogOpen(false);
      setUrl("");
      refetch();

      toast("Syncing started, it will take a few minutes");
    } catch (error) {
      console.error("Error starting crawl:", error);
      alert("Failed to start crawling. Please try again.");
    }
  }

  async function handleFileUpload() {
    if (!selectedFile) return;

    try {
      const result = await uploadFile({
        file: selectedFile,
        title: fileTitle || undefined,
        brand: selectedBrand || undefined,
        knowledgeType,
        noAccessToRelease: !allowFullAccess,
      });

      setIsDialogOpen(false);
      setSelectedFile(null);
      setFileTitle("");
      refetch();

      toast("File uploaded successfully");
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file. Please try again.");
    }
  }

  const isSyncing = jobs?.some((job) => job.status === "SYNCING");

  const handleConfigureZendesk = useCallback(() => {
    setIsConfigureDialogOpen(true);
  }, []);

  const handleCloseConfigureDialog = useCallback(() => {
    setIsConfigureDialogOpen(false);
  }, []);

  const handleSaveConfigureDialog = useCallback(() => {
    setIsConfigureDialogOpen(false);
    refetch();
  }, [refetch]);

  const handleConfigureZendeskTicket = useCallback(() => {
    setIsConfigureTicketDialogOpen(true);
  }, []);

  const handleCloseConfigureTicketDialog = useCallback(() => {
    setIsConfigureTicketDialogOpen(false);
  }, []);

  const handleSaveConfigureTicketDialog = useCallback(() => {
    setIsConfigureTicketDialogOpen(false);
    refetch();
  }, [refetch]);

  const handleConfigureAccess = useCallback((integration: Integration) => {
    setSelectedIntegrationForAccess(integration);
    setIsConfigureAccessDialogOpen(true);
  }, []);

  const handleCloseConfigureAccessDialog = useCallback(() => {
    setIsConfigureAccessDialogOpen(false);
    setSelectedIntegrationForAccess(null);
  }, []);

  const handleSaveConfigureAccessDialog = useCallback(() => {
    setIsConfigureAccessDialogOpen(false);
    setSelectedIntegrationForAccess(null);
    refetch();
  }, [refetch]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isSyncing) {
      intervalId = setInterval(() => {
        refetch();
      }, 5000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isSyncing, refetch]);

  // Maps MIME types to human-readable file type strings
  const FILE_TYPE_LABELS: Record<string, string> = {
    "application/pdf": "PDF",
    "application/msword": "DOC",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "DOCX",
    "text/markdown": "Markdown",
    "text/plain": "TXT",
    "image/png": "PNG",
    "image/jpeg": "JPEG",
    "image/gif": "GIF",
    "application/vnd.ms-excel": "XLS",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
    // Add more mappings as needed
  };

  const getFileTypeLabel = useCallback((mimeType: string) => {
    return (
      FILE_TYPE_LABELS[mimeType] ||
      mimeType.split("/")[1]?.toUpperCase() ||
      "File"
    );
  }, []);

  return (
    <StepMain>
      <StepContent
        direction={"column"}
        gap={"16px"}
        className={styles.SelectIntegration}
      >
        <StepHeader
          title={title ?? integrationTypeToContent[type].title}
          description={
            description ?? integrationTypeToContent[type].description
          }
        />

        <Grid
          columns="2"
          gap="3"
          display="inline-grid"
          width={"100%"}
          height={"fit-content"}
        >
          {options?.map((item) => {
            const { title, svg } = integrationNameToGalleryItem[item];
            const isExists = existingIntegrations.some(
              (integ) =>
                integ.type === type &&
                integ.name === item &&
                isIntegrationCompleted(integ),
            );

            const isNotAvaiable = existingIntegrations.some(
              (integ) =>
                integ.type === type &&
                integ.name === item &&
                isIntegrationNotAvailable(integ),
            );

            const isSetupNotSupported =
              (type === "TICKET_SYSTEM" &&
                !isExists &&
                !isNotAvaiable &&
                !supportedTicketSystems.includes(
                  item as TicketSystemIntegrationName,
                )) ||
              (type === "WIKI" &&
                !isExists &&
                !isNotAvaiable &&
                !supportedWiki.includes(item as WikiIntegrationName));

            const isSingleIntegrationExists =
              !isMultipleAllowed &&
              existingIntegrations.some((integ) => integ.type === type);

            return (isSingleIntegrationExists && isExists) ||
              !isSingleIntegrationExists ? (
              <Card
                key={item}
                size="2"
                className={cx(styles.Card, {
                  [styles.exists]:
                    isExists ||
                    isNotAvaiable ||
                    isSingleIntegrationExists ||
                    isSetupNotSupported,
                })}
                onClick={
                  isExists ||
                  isNotAvaiable ||
                  isSingleIntegrationExists ||
                  isSetupNotSupported
                    ? undefined
                    : () => onClick(item, type)
                }
              >
                <Flex direction={"column"} gap={"15px"}>
                  <Flex gap={"15px"} justify={"between"}>
                    <div>{svg}</div>

                    <Flex gap="2" align="center">
                      {isExists && (
                        <Badge color="green" size={"2"}>
                          <CheckIcon />
                          Connected
                        </Badge>
                      )}

                      {isNotAvaiable && (
                        <Badge color="red" size={"2"}>
                          Connection Failed
                        </Badge>
                      )}

                      {isSetupNotSupported && (
                        <Badge color="gray" size={"2"}>
                          Coming Soon
                        </Badge>
                      )}

                      {/* Three dots menu for connected knowledge base and wiki integrations */}
                      {isExists &&
                        (type === "KNOWLEDGE_BASE" || type === "WIKI") &&
                        (() => {
                          const menuItems = [
                            ...(item === "zendesk" && type === "KNOWLEDGE_BASE"
                              ? [
                                  {
                                    label: "Configure Categories",
                                    onClick: handleConfigureZendesk,
                                  },
                                ]
                              : []),
                            ...([
                                  {
                                    label: "Configure Access",
                                    onClick: () => {
                                      const integration =
                                        existingIntegrations.find(
                                          (integ) =>
                                            integ.type === type &&
                                            integ.name === item &&
                                            isIntegrationCompleted(integ),
                                        );
                                      if (integration) {
                                        handleConfigureAccess(integration);
                                      }
                                    },
                                  },
                                ]),
                          ];

                          if (!menuItems.length) {
                            return null;
                          }

                          return (
                            <div
                              style={{
                                pointerEvents: "auto",
                                position: "relative",
                              }}
                            >
                              <DropdownMenu
                                items={menuItems}
                                side="bottom"
                                align="end"
                                portal={true}
                              >
                                <DropdownMenu.Trigger>
                                  <Button
                                    variant="ghost"
                                    color="gray"
                                    size="1"
                                    style={{
                                      padding: "4px",
                                      cursor: "pointer",
                                      pointerEvents: "auto",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  >
                                    <DotsHorizontalIcon
                                      width="16"
                                      height="16"
                                    />
                                  </Button>
                                </DropdownMenu.Trigger>
                              </DropdownMenu>
                            </div>
                          );
                        })()}

                      {/* Three dots menu for connected Zendesk ticket system integration */}
                      {isExists &&
                        item === "zendesk" &&
                        type === "TICKET_SYSTEM" && (
                          <div
                            style={{
                              pointerEvents: "auto",
                              position: "relative",
                            }}
                          >
                            <DropdownMenu
                              items={[
                                {
                                  label: "Configure Groups",
                                  onClick: handleConfigureZendeskTicket,
                                },
                              ]}
                              side="bottom"
                              align="end"
                              portal={true}
                            >
                              <DropdownMenu.Trigger>
                                <Button
                                  variant="ghost"
                                  color="gray"
                                  size="1"
                                  style={{
                                    padding: "4px",
                                    cursor: "pointer",
                                    pointerEvents: "auto",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                >
                                  <DotsHorizontalIcon width="16" height="16" />
                                </Button>
                              </DropdownMenu.Trigger>
                            </DropdownMenu>
                          </div>
                        )}
                    </Flex>
                  </Flex>

                  <Flex justify="between">
                    <Text size={"3"} weight={"medium"} as={"div"}>
                      {title}
                    </Text>
                    {isNotAvaiable && (
                      <div
                        style={{
                          pointerEvents: "auto",
                          position: "relative",
                        }}
                      >
                        <Button
                          variant="soft"
                          color="gray"
                          highContrast
                          className=" z-50"
                          size="1"
                          onClick={(e) => {
                            e.stopPropagation();
                            const integration = existingIntegrations.find(
                              (integ) =>
                                integ.type === type &&
                                integ.name === item &&
                                isIntegrationNotAvailable(integ),
                            );
                            if (integration?.id) {
                              deleteIntegration(integration.id);
                            } else {
                              toast("Integration not found");
                            }
                          }}
                        >
                          Delete Integration
                        </Button>
                      </div>
                    )}
                  </Flex>
                </Flex>
              </Card>
            ) : null;
          })}

          {type === "KNOWLEDGE_BASE" && (
            <>
              {jobs?.map((job) => {
                return (
                  <Card
                    size="2"
                    className={cx(styles.Card, " pointer-events-auto", {})}
                    key={job.id}
                  >
                    <Flex direction={"column"} gap={"15px"}>
                      <Flex gap={"15px"} justify={"between"}>
                        <div>
                          <GlobeIcon className=" w-12 h-12 text-gray-500" />
                        </div>

                        <Flex gap="2">
                          {brands
                            ?.filter((x: any) => String(x.id) === job.brand)
                            ?.map((x: any) => (
                              <Badge key={x.id} color="blue" size={"2"}>
                                {x.name}
                              </Badge>
                            ))}
                          {job.status === "FINISHED" && (
                            <Badge color="green" size={"2"}>
                              <CheckIcon />
                              Last synced:{" "}
                              {new Date(job.updatedAt).toLocaleDateString()}
                            </Badge>
                          )}

                          {job.status === "SYNCING" && (
                            <Badge color="gray" size={"2"}>
                              <Spinner size={"2"} />
                              Syncing
                            </Badge>
                          )}
                        </Flex>
                      </Flex>

                      <Flex gap="2" justify="between">
                        <Text size={"3"} weight={"medium"} as={"div"}>
                          {job.url}
                        </Text>

                        {job.status === "FINISHED" && (
                          <Button
                            variant="soft"
                            color="gray"
                            highContrast
                            className=" z-50"
                            size="1"
                            onClick={() => {
                              setUrl(job.url);
                              handleSubmit(job.url);
                              refetch();
                            }}
                          >
                            Resync
                          </Button>
                        )}
                      </Flex>
                    </Flex>
                  </Card>
                );
              })}

              <Card
                className="mb-4 p-4 hover:bg-gray-50 cursor-pointer "
                onClick={() => setIsDialogOpen(true)}
              >
                <Flex align="center" gap="3">
                  <Plus className="w-5 h-5 text-gray-500" />
                  <Text size="3" weight="medium">
                    Add Public Knowledge
                  </Text>
                </Flex>
              </Card>
            </>
          )}
        </Grid>
      </StepContent>

      <Dialog.Root open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <Dialog.Content style={{ maxWidth: 450 }}>
          <Dialog.Title>Add Public Knowledge</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Add knowledge from a URL or upload a file (PDF, DOCX, or Markdown).
          </Dialog.Description>

          <Flex direction="column" gap="3">
            <Flex gap="2">
              <Tabs
                value={uploadMode}
                onValueChange={(value) =>
                  setUploadMode(value as "url" | "file")
                }
              >
                <TabsList>
                  <TabsTrigger value={"url"}>From URL</TabsTrigger>

                  <TabsTrigger value={"file"}>Upload File</TabsTrigger>
                </TabsList>
              </Tabs>
            </Flex>

            {uploadMode === "url" && (
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com"
                className="w-full p-2 border rounded"
              />
            )}

            {uploadMode === "file" && (
              <Flex direction="column" gap="3">
                <KnowledgeFileUploadComponent
                  variant="basic"
                  accept={[".pdf", ".docx", ".md"]}
                  onFileUpload={(file) => {
                    validateFile(file, {
                      accept: [".pdf", ".docx", ".md"],
                      maxSizeInBytes: 30 * 1024 * 1024,
                    });

                    setSelectedFile(file);

                    return Promise.resolve({
                      name: file.name,
                      path: file.name,
                    });
                  }}
                  onSuccess={(filePath) => {}}
                />
                {selectedFile && (
                  <div className="flex items-center gap-2 border border-gray-200 rounded-md p-2 relative">
                    <FileIcon className="w-8 h-8 text-gray-500" />
                    <Flex direction="column" gap="1">
                      <Text size="1" weight="medium">
                        {selectedFile?.name}
                      </Text>
                      <Text size="2" weight="medium">
                        {getFileTypeLabel(selectedFile?.type || "")}
                      </Text>
                    </Flex>

                    <Button
                      variant="ghost"
                      color="gray"
                      size="1"
                      className="absolute top-0 right-0 rounded-full w-6 h-6 p-0 m-0 "
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                      }}
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </Button>
                  </div>
                )}
              </Flex>
            )}
          </Flex>
          {uploadMode === "file" && (
            <Flex direction="column" gap="2" mt="4">
              <Text size="2" weight="medium">
                What information is it?
              </Text>
              <Tabs
                value={knowledgeType}
                onValueChange={(value) => {
                  setKnowledgeType(value as "kb" | "wiki");
                }}
              >
                <TabsList>
                  <TabsTrigger value="kb">Knowledge (external)</TabsTrigger>
                  <TabsTrigger value="wiki">Wiki (internal)</TabsTrigger>
                </TabsList>
              </Tabs>
            </Flex>
          )}
          {uploadMode === "url" && (
            <FieldGroup
              label={"Syncing options"}
              description={
                "Check this if you want to crawl only the specific page - if not, Quack will crawl the entire website"
              }
            >
              <Checkbox
                label="Sync only 1 page"
                value={isSyncOnlyOnePage}
                onChange={(checked) => setIsSyncOnlyOnePage(checked)}
              />
            </FieldGroup>
          )}

          <Flex direction="column" gap="2" mt="4">
            <Flex align="center" justify="between" gap="3">
              <Text size="2" weight="medium" as="label">
                Allow AI agents full access to new articles
              </Text>
              <Switch value={allowFullAccess} onChange={setAllowFullAccess} />
            </Flex>
          </Flex>
          
          <br />
          <Collapsable
            title={"Advanced"}
            initialCollapsedValue={false}
            size="small"
          >
            <FieldGroup
              label={"Load for specific brand"}
              description={
                "Choose the brand to add this knowledge base to ( for all brands, keep it empty )"
              }
            >
              <Flex gap="2" align="center">
                <Select
                  value={selectedBrand ?? ""}
                  onChange={(newValue) => {
                    setSelectedBrand(newValue);
                  }}
                  options={brands?.map((brand: any) => ({
                    label: brand.name,
                    value: brand.id,
                  }))}
                  classes={{
                    trigger: styles.Select,
                    value: styles.SelectValue,
                  }}
                  portal={false}
                />
                {selectedBrand && (
                  <Button
                    variant="ghost"
                    color="gray"
                    size="1"
                    highContrast
                    onClick={() => {
                      setSelectedBrand(null);
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Flex>
            </FieldGroup>
          </Collapsable>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray" highContrast>
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              color="gray"
              variant="solid"
              highContrast
              onClick={
                uploadMode === "url" ? () => handleSubmit() : handleFileUpload
              }
              disabled={uploadMode === "url" ? !url : !selectedFile}
              loading={uploadMode === "url" ? isSyncing : isUploading}
            >
              {uploadMode === "url" ? "Add" : "Upload"}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {isConfigureDialogOpen && (
        <ZendeskKnowledgeIntegration
          onClose={handleCloseConfigureDialog}
          onSave={handleSaveConfigureDialog}
        />
      )}

      {isConfigureTicketDialogOpen && (
        <ZendeskTicketIntegration
          onClose={handleCloseConfigureTicketDialog}
          onSave={handleSaveConfigureTicketDialog}
        />
      )}

      {isConfigureAccessDialogOpen && selectedIntegrationForAccess && (
        <ConfigureAccessDialog
          integration={selectedIntegrationForAccess}
          onClose={handleCloseConfigureAccessDialog}
          onSave={handleSaveConfigureAccessDialog}
        />
      )}

      <StepFooter>
        {onClose != null && (
          <Button color="gray" variant="solid" highContrast onClick={onClose}>
            Done
          </Button>
        )}
      </StepFooter>
    </StepMain>
  );
}

export default SelectIntegration;
